import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { useAppTheme, useCreateExperience } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { OUTCOMES, STARTERS, templateDoc, type Outcome, type Template } from '@/templates';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Check, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const SURFACE_HINT: Record<string, string> = {
  modal: 'Centered modal',
  fullscreen: 'Full-screen',
  banner: 'Banner',
  bottom_sheet: 'Bottom sheet',
  inline: 'Inline content',
};

const FAMILY = {
  overlay: {
    title: 'Overlay',
    description: 'Moments that appear over the app: modals, full-screen steps, sheets, and banners.',
  },
  embedded: {
    title: 'Embedded',
    description: 'Content that lives inside a named slot in the host app’s own screen.',
  },
} as const;

function WireLine({ width = 'w-full' }: { width?: string }): React.JSX.Element {
  return <span className={cn('block h-1 rounded-full bg-zinc-300 dark:bg-zinc-600', width)} />;
}

/** Gallery previews are intentionally structural wireframes. Rendering the real artboard at 26%
 * made text and imagery turn into visual noise, and it implied a design fidelity the template card
 * cannot communicate at this size. The wireframe shows the useful decision instead: where and how
 * the experience appears in the host app. */
function TemplatePreview({ template }: { template: Template }): React.JSX.Element {
  const surface = template.steps[0]!.surface;
  const multiStep = template.steps.length > 1;

  const hostContent = (
    <>
      <WireLine width="w-5/6" />
      <WireLine width="w-2/3" />
      <span className="mt-1 block h-6 rounded bg-zinc-100 dark:bg-zinc-800" />
      <WireLine />
      <WireLine width="w-3/4" />
    </>
  );

  const experience = (() => {
    if (template.blank) {
      const placement = surface === 'banner'
        ? 'inset-x-1 top-1 h-7'
        : surface === 'bottom_sheet'
          ? 'inset-x-1 bottom-1 h-14 rounded-b-[9px]'
          : surface === 'fullscreen'
            ? 'inset-1'
            : surface === 'inline'
              ? 'inset-x-2 top-[47px] h-10'
              : 'inset-x-2 top-[38px] h-14';
      return <span className={cn('absolute rounded-md border border-dashed border-indigo-400/80 bg-indigo-500/[0.04]', placement)} />;
    }

    if (surface === 'fullscreen') {
      return (
        <div className="absolute inset-1 flex flex-col rounded-[9px] bg-indigo-50 p-1.5 dark:bg-indigo-950/50">
          <div className="flex gap-0.5">
            {(multiStep ? [0, 1, 2] : [0]).map((item) => <span key={item} className="h-0.5 flex-1 rounded-full bg-indigo-300 dark:bg-indigo-600" />)}
          </div>
          <span className="mt-2 block flex-1 rounded bg-indigo-100 dark:bg-indigo-900" />
          <WireLine width="mt-2 w-5/6" />
          <WireLine width="mt-1 w-2/3" />
          <span className="mt-2 block h-4 rounded bg-indigo-500" />
        </div>
      );
    }

    if (surface === 'bottom_sheet') {
      return (
        <div className="absolute inset-x-1 bottom-1 rounded-b-[9px] rounded-t-lg border border-zinc-200 bg-white p-2 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
          <span className="mx-auto mb-2 block h-0.5 w-5 rounded-full bg-zinc-300 dark:bg-zinc-600" />
          <WireLine width="w-4/5" />
          <WireLine width="mt-1 w-full" />
          <span className="mt-2 block h-3 rounded bg-indigo-500" />
        </div>
      );
    }

    if (surface === 'banner') {
      return (
        <div className="absolute inset-x-1 top-1 flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 p-1.5 shadow-sm dark:border-indigo-800 dark:bg-indigo-950">
          <span className="size-3 shrink-0 rounded bg-indigo-200 dark:bg-indigo-800" />
          <span className="min-w-0 flex-1 space-y-1"><WireLine /><WireLine width="w-2/3" /></span>
        </div>
      );
    }

    if (surface === 'inline') {
      return (
        <div className="absolute inset-x-2 top-[43px] overflow-hidden rounded-md border border-indigo-200 bg-indigo-50 p-1.5 dark:border-indigo-800 dark:bg-indigo-950">
          <div className="flex gap-1">
            <span className="h-7 w-9 shrink-0 rounded bg-indigo-200 dark:bg-indigo-800" />
            <span className="h-7 w-9 shrink-0 rounded bg-indigo-100 ring-1 ring-indigo-200 dark:bg-indigo-900 dark:ring-indigo-800" />
            <span className="h-7 w-9 shrink-0 rounded bg-indigo-100 dark:bg-indigo-900" />
          </div>
          <div className="mt-1 flex justify-center gap-0.5"><span className="size-1 rounded-full bg-indigo-500" /><span className="size-1 rounded-full bg-indigo-200" /></div>
        </div>
      );
    }

    return (
      <div className="absolute inset-x-2 top-[34px] rounded-lg border border-zinc-200 bg-white p-2 shadow-md dark:border-zinc-700 dark:bg-zinc-900">
        <span className="mb-2 block h-7 rounded bg-indigo-100 dark:bg-indigo-900" />
        <WireLine width="w-4/5" />
        <WireLine width="mt-1 w-full" />
        <span className="mt-2 block h-3 rounded bg-indigo-500" />
      </div>
    );
  })();

  return (
    <div
      className="relative grid h-40 place-items-center overflow-hidden bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-900 dark:to-zinc-950"
      data-testid="template-wireframe"
      data-template={template.id}
      aria-hidden="true"
    >
      <div className="absolute inset-x-0 bottom-0 h-px bg-border/70" />
      <div className="relative h-[132px] w-[76px] rounded-[15px] border-2 border-zinc-300 bg-white p-2 pt-4 shadow-sm dark:border-zinc-600 dark:bg-zinc-950">
        <span className="absolute left-1/2 top-1.5 h-1 w-5 -translate-x-1/2 rounded-full bg-zinc-300 dark:bg-zinc-600" />
        <div className="space-y-1.5">{hostContent}</div>
        {experience}
      </div>
      {template.blank ? (
        <div className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-background/90 px-2.5 py-1 text-[9px] font-medium text-muted-foreground shadow-sm ring-1 ring-border/70 backdrop-blur">
          Blank canvas
        </div>
      ) : null}
    </div>
  );
}

function TemplateCard({ template, onChoose }: {
  template: Template;
  onChoose: (template: Template) => void;
}): React.JSX.Element {
  const first = template.steps[0]!;
  const surface = new Set(template.steps.map((step) => step.surface)).size > 1
    ? 'Mixed overlay surfaces'
    : SURFACE_HINT[first.surface] ?? first.surface;

  return (
    <button
      type="button"
      onClick={() => onChoose(template)}
      className="group overflow-hidden rounded-xl border bg-background text-left transition-all hover:-translate-y-0.5 hover:border-indigo-400 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      aria-label={`Choose ${template.name}`}
    >
      <TemplatePreview template={template} />
      <span className="flex min-h-44 flex-col p-4">
        <span className="flex flex-wrap items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span className="rounded-full bg-secondary px-2 py-1">{template.pattern}</span>
          <span>{surface}</span>
          <span aria-hidden="true">·</span>
          <span>{template.steps.length} {template.steps.length === 1 ? 'step' : 'steps'}</span>
        </span>
        <span className="mt-3 text-sm font-semibold text-foreground">{template.name}</span>
        <span className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">{template.description}</span>
        {template.prerequisites.length ? (
          <span className="mt-3 flex flex-wrap gap-1.5">
            {template.prerequisites.map((item) => (
              <span key={item} className="rounded border border-amber-500/30 bg-amber-500/5 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                {item}
              </span>
            ))}
          </span>
        ) : (
          <span className="mt-3 inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400">
            <Check className="size-3" /> No setup required
          </span>
        )}
        <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-medium text-indigo-600">
          {template.blank ? 'Start blank' : 'Use template'}
          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </span>
    </button>
  );
}

export function TemplateGallery(): React.JSX.Element {
  const appId = useContextStore((state) => state.appId);
  const { data: theme, isLoading } = useAppTheme(appId);
  const create = useCreateExperience(appId);
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome | 'all'>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Template | null>(null);
  const [name, setName] = useState('');

  const visible = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return STARTERS.filter((template) => {
      const matchesOutcome = outcome === 'all' || template.outcomes.includes(outcome);
      const haystack = `${template.name} ${template.pattern} ${template.description}`.toLowerCase();
      return matchesOutcome && (!normalized || haystack.includes(normalized));
    });
  }, [outcome, query]);

  const choose = (template: Template): void => {
    setSelected(template);
    setName(template.name);
  };

  const start = (): void => {
    if (!selected || !name.trim() || create.isPending) return;
    create.mutate(
      { name: name.trim(), document: templateDoc(selected, theme) },
      {
        onSuccess: (experience) => void navigate({ to: '/experiences/$id/edit', params: { id: experience.id } }),
        onError: (error: Error) => toast.error(error.message),
      }
    );
  };

  if (isLoading) return <Skeleton className="h-96 w-full rounded-lg" />;

  if (selected) {
    return (
      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-xl border bg-secondary/20">
          <TemplatePreview template={selected} />
          <div className="space-y-2 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Selected starting point</p>
            <p className="font-semibold">{selected.name}</p>
            <p className="text-xs leading-5 text-muted-foreground">
              {selected.pattern} · {SURFACE_HINT[selected.steps[0]!.surface]} · {selected.steps.length}{' '}
              {selected.steps.length === 1 ? 'step' : 'steps'}
            </p>
          </div>
        </div>

        <div className="flex flex-col justify-center rounded-xl border p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Last step</p>
          <h3 className="mt-2 text-lg font-semibold">Name your experience</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Your current app and environment stay selected. You can rename the experience later.
          </p>
          <div className="mt-6 space-y-2">
            <Label htmlFor="experience-name">Experience name</Label>
            <Input
              id="experience-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && start()}
              autoFocus
              maxLength={120}
            />
          </div>
          <div className="mt-6 flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={() => setSelected(null)}>
              <ArrowLeft className="size-4" /> Back to templates
            </Button>
            <Button onClick={start} disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create experience'}
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-7">
      <section aria-labelledby="outcome-heading" className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p id="outcome-heading" className="text-sm font-semibold">1. What do you want to accomplish?</p>
            <p className="mt-1 text-xs text-muted-foreground">Choose an outcome, then pick a starting point.</p>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by outcome">
            <button
              type="button"
              aria-pressed={outcome === 'all'}
              onClick={() => setOutcome('all')}
              className={cn('rounded-full px-3 py-1.5 text-xs font-medium transition-colors', outcome === 'all' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-secondary text-muted-foreground hover:bg-secondary/70 hover:text-foreground')}
            >
              All outcomes
            </button>
            {OUTCOMES.map((item) => (
              <button
                key={item}
                type="button"
                aria-pressed={outcome === item}
                onClick={() => setOutcome(item)}
                className={cn('rounded-full px-3 py-1.5 text-xs font-medium transition-colors', outcome === item ? 'bg-indigo-600 text-white shadow-sm' : 'bg-secondary text-muted-foreground hover:bg-secondary/70 hover:text-foreground')}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold">2. Choose a starting point</p>
          <p className="mt-1 text-xs text-muted-foreground">Templates are valid native documents, not decorative mockups.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search templates" className="pl-9" />
        </div>
      </div>

      {(['overlay', 'embedded'] as const).map((family) => {
        const templates = visible.filter((template) => template.family === family && !template.blank);
        const blanks = visible.filter((template) => template.family === family && template.blank);
        if (!templates.length && !blanks.length) return null;
        return (
          <section key={family} aria-labelledby={`${family}-templates`} className="space-y-4">
            <div>
              <h3 id={`${family}-templates`} className="font-semibold">{FAMILY[family].title}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{FAMILY[family].description}</p>
            </div>
            {templates.length ? (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {templates.map((template) => <TemplateCard key={template.id} template={template} onChoose={choose} />)}
              </div>
            ) : null}
            {blanks.length ? (
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Blank starts</p>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {blanks.map((template) => <TemplateCard key={template.id} template={template} onChoose={choose} />)}
                </div>
              </div>
            ) : null}
          </section>
        );
      })}

      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed px-6 py-12 text-center">
          <p className="text-sm font-medium">No starting points match these filters.</p>
          <Button variant="ghost" size="sm" className="mt-3" onClick={() => { setOutcome('all'); setQuery(''); }}>
            Clear filters
          </Button>
        </div>
      ) : null}
    </div>
  );
}
